import {
  Box,
  Text,
  Accordion,
  AccordionButton,
  Flex,
  AccordionIcon,
  AccordionPanel,
  AccordionItem,
  Button,
  Skeleton,
} from '@everlywell/ui-kit';
import DynamicIcon from 'components/DynamicIcon';
import React, { MouseEventHandler, useState } from 'react';
import type { UseFormMethods } from 'react-hook-form/dist/types';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { Promo } from 'utils/helpers/activePromoHelpers';
import useAdvertisedPromos from 'utils/hooks/useAdvertisedPromos';

import PromoCodeSubmission from './components/PromoCodeSubmission/PromoCodeSubmission';

export type PromoFormValues = {
  promoCode: string;
};

type PromoCodeProps = {
  promotion: any;
  showLoadingState?: boolean;
  isLoading?: boolean;
  applyPromoMutation?: any;
  removePromoCodeHandler: MouseEventHandler<HTMLButtonElement>;
  handleSubmit: UseFormMethods<PromoFormValues>['handleSubmit'];
  formState: UseFormMethods<PromoFormValues>['formState'];
  control: UseFormMethods<PromoFormValues>['control'];
  promoCodeSubmitHandler: (PromoFormValues: PromoFormValues) => void;
  showAdvertisedPromos?: boolean;
  /**
   * Shows lightGrey border instead of uiGrey
   */
  isVariant?: boolean;
};

const PromoCode = ({
  promotion,
  showLoadingState,
  isLoading,
  applyPromoMutation,
  removePromoCodeHandler,
  handleSubmit,
  formState,
  control,
  promoCodeSubmitHandler,
  showAdvertisedPromos = false,
  isVariant = false,
}: PromoCodeProps) => {
  const [promoMessage, setPromoMessage] = useState('');
  const [wasAdvertisedPromoSubmitted, setWasAdvertisedPromoSubmitted] =
    useState(false);
  const [promoCodeInputValue, setPromoCodeInputValue] = useState('');

  const { advertisedPromos, isLoading: isLoadingPromos } = useAdvertisedPromos({
    isEnabled: showAdvertisedPromos,
  });

  const getDiscountText = (promo: Promo) => {
    if (promo.discounts.flat_percent) {
      return `${promo.discounts.flat_percent}% Discount`;
    }

    // Get the best tiered discount
    const tiers = promo.discounts.tiered;
    if (tiers && tiers.length > 0) {
      const bestTier = tiers.reduce(
        (max, tier) => (tier.percent > max.percent ? tier : max),
        tiers[0],
      );
      return `Up to ${bestTier.percent}% Discount`;
    }
  };

  const handlePromoCodeSubmit = (promoFormValues: PromoFormValues) => {
    promoCodeSubmitHandler(promoFormValues);
    setPromoMessage(`${promoFormValues.promoCode} promo code applied`);
  };

  const trackAdvertisedPromoClick = (promoCode: string) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: 'Advertised promo code submitted',
        promoCode,
      },
    });
  };

  const trackAccordionClick = (isOpen: boolean) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_ACCORDION,
      data: {
        label: 'Accordion promo code clicked',
        action: isOpen ? 'to open' : 'to close',
      },
    });
  };

  const advertisedPromosElement =
    showAdvertisedPromos && advertisedPromos.length > 0 ? (
      <Flex gap="2" marginTop="2" flexDirection="column">
        {advertisedPromos?.map((promo) => (
          <Flex
            key={promo.id}
            justifyContent="space-between"
            alignItems="center"
            padding="3"
            width="100%"
            backgroundColor="tints.white"
            borderRadius="xl"
            minHeight="56px"
            gap="3"
            onClick={() => {
              setPromoCodeInputValue(promo.code);
              handlePromoCodeSubmit({ promoCode: promo.code });
              setWasAdvertisedPromoSubmitted(true);
              trackAdvertisedPromoClick(promo.code);
            }}
            _hover={{
              cursor: 'pointer',
            }}
          >
            <Flex flexDirection="column">
              <Text fontSize="md" fontWeight="medium">
                {getDiscountText(promo)}
              </Text>
              <Text fontSize="sm" color="tints.darkGrey">
                {promo?.description || 'Applicable on purchase'}
              </Text>
            </Flex>
            <Button
              height="32px"
              paddingX="2 !important" // To override the default padding
              paddingY="0.5 !important" // To override the default padding
              backgroundColor="tints.cream"
              color="tints.black"
              variant="secondary"
              boxShadow="none"
              borderRadius="lg"
              _active={{
                boxShadow: 'inset 0 0 0 2px tints.darkGrey',
                color: 'tints.darkGrey',
              }}
              _hover={{
                cursor: 'pointer',
              }}
              fontSize={['md']}
              aria-label={`Apply promo code ${promo.code}`}
            >
              {promo?.code?.toUpperCase() || 'Apply'}
            </Button>
          </Flex>
        ))}
      </Flex>
    ) : null;

  const promoCodeSubmissionElement = (
    <PromoCodeSubmission
      promoMessage={promoMessage}
      promotion={promotion}
      showLoadingState={showLoadingState}
      isLoading={isLoading}
      applyPromoMutation={applyPromoMutation}
      removePromoCodeHandler={removePromoCodeHandler}
      handlePromoCodeSubmit={handleSubmit(handlePromoCodeSubmit)}
      formState={formState}
      control={control}
      advertisedPromosElement={advertisedPromosElement}
      wasAdvertisedPromoSubmitted={wasAdvertisedPromoSubmitted}
      promoCodeInputValue={promoCodeInputValue}
      setPromoCodeInputValue={setPromoCodeInputValue}
      shouldShowInputIcon={!showAdvertisedPromos}
      isVariant={isVariant}
    />
  );

  return showAdvertisedPromos ? (
    <Accordion
      backgroundColor="tints.cream"
      // Expand by default if there is a promotion applied
      defaultIndex={promotion ? 0 : undefined}
      borderRadius="xl"
      padding="3"
      allowToggle
    >
      <AccordionItem>
        {({ isExpanded }) => (
          <>
            <AccordionButton
              padding="0"
              marginBottom={isExpanded ? 4 : 0}
              sx={{
                // Styles for the chevron icon
                svg: {
                  color: 'tints.uiGrey',
                },
              }}
              onClick={() => trackAccordionClick(isExpanded)}
            >
              <Flex
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Flex gap="1">
                  <DynamicIcon icon="Tag" />
                  <Text>Add a promo code</Text>
                </Flex>

                {advertisedPromos.length > 0 && !isLoadingPromos ? (
                  <Text>{`${advertisedPromos.length} available`}</Text>
                ) : null}

                {isLoadingPromos ? (
                  <Skeleton height="24px" borderRadius="md" fitContent>
                    <Box width="80px" marginRight="1" />
                  </Skeleton>
                ) : null}
              </Flex>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel paddingBottom="0">
              {promoCodeSubmissionElement}
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  ) : (
    promoCodeSubmissionElement
  );
};

export default PromoCode;
